import type { ReactNode } from 'react';

import useWorkspaceStore from '@/stores/WorkspaceStore';
import { LANGUAGE } from '@/types/commonConst';

import { type CommonMessageKey, getCommonMessage } from './common';
import { type FeedbackMessageKey, getFeedbackMessage } from './feedback';
import { getHeySynclyMessage, type HeySynclyMessageKey } from './hey-syncly';
import { getScrapingMessage, type ScrapingMessageKey } from './scraping';
import { getTopicMessage, type TopicMessageKey } from './topic';
import { getWorkflowMessage, type WorkflowMessageKey } from './workflow';

export const getMessageMap = (lang: LANGUAGE) => {
  return {
    common: (key: CommonMessageKey) => getCommonMessage(lang, key),
    feedback: (key: FeedbackMessageKey) => getFeedbackMessage(lang, key),
    workflow: (key: WorkflowMessageKey) => getWorkflowMessage(lang, key),
    topic: (key: TopicMessageKey) => getTopicMessage(lang, key),
    heySyncly: (key: HeySynclyMessageKey) => getHeySynclyMessage(lang, key),
    scraping: (
      key: ScrapingMessageKey,
      params?: Record<string, string | number>,
    ) => getScrapingMessage(lang, key, params),
  };
};

export const useMessage = () => {
  const navLang = navigator.language.split('-')?.[0] || LANGUAGE.EN;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const lang: LANGUAGE =
    currWorkspace?.language || (navLang as LANGUAGE) || LANGUAGE.EN;
  return getMessageMap(lang);
};

export const getMessage = () => {
  const navLang = navigator.language.split('-')?.[0] || LANGUAGE.EN;
  const currWorkspace = useWorkspaceStore.getState().currWorkspace;
  const lang = currWorkspace?.language || (navLang as LANGUAGE) || LANGUAGE.EN;
  return getMessageMap(lang);
};

export const m = {
  get common() {
    return getMessage().common;
  },
  get feedback() {
    return getMessage().feedback;
  },
  get workflow() {
    return getMessage().workflow;
  },
  get topic() {
    return getMessage().topic;
  },
  get heySyncly() {
    return getMessage().heySyncly;
  },
};

export const getFeatureMessage = <T = Record<string, string | number>>(
  messages: Partial<Record<LANGUAGE, MessageValue<T>>> | undefined,
  lang: LANGUAGE,
  params?: T,
): ReactNode => {
  const message = messages?.[lang] ?? messages?.[LANGUAGE.EN];
  if (!message) return '';

  if (typeof message === 'function') {
    return message(params || ({} as T));
  }
  return message;
};

export type MessageValue<T = Record<string, string | number>> =
  | (ReactNode | string)
  | ((params: T) => ReactNode | string);

export type MessageMap = Record<
  string,
  Partial<Record<LANGUAGE, MessageValue>>
>;
